/**
 * Widget-API-Client — sehr schlank. Nutzt fetch() + ReadableStream fuer
 * SSE statt EventSource (das kann nur GET).
 */

export interface WidgetConfig {
  widget: {
    publicId: string;
    name: string;
    defaultLanguage: string;
    accentColor: string | null;
    headerStyle: string | null;
    showHint: boolean;
    availableLanguages: string[];
  };
  locale: {
    language: string;
    welcomeTitle: string | null;
    welcomeSubtitle: string | null;
    hintText: string | null;
    quickActions: Array<{ key: string; icon?: string; label: string; description?: string; prompt?: string }>;
    suggestions: string[];
    composerPlaceholder: string | null;
  } | null;
}

export interface ApiClientOptions {
  apiBase: string;
  publicId: string;
  visitorToken: string;
}

export class ApiClient {
  constructor(private opts: ApiClientOptions) {}

  async getConfig(language?: string): Promise<WidgetConfig> {
    const url = new URL(`/api/web-chat/${this.opts.publicId}/config`, this.opts.apiBase);
    if (language) url.searchParams.set('lang', language);
    const r = await fetch(url.toString(), { credentials: 'omit' });
    if (!r.ok) throw new Error(`Config-Request: ${r.status}`);
    return r.json();
  }

  async createSession(language: string): Promise<string> {
    const url = `${this.opts.apiBase}/api/web-chat/${this.opts.publicId}/sessions`;
    const r = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language, visitorToken: this.opts.visitorToken }),
    });
    if (!r.ok) throw new Error(`Session-Create: ${r.status}`);
    const json = (await r.json()) as { sessionId: string };
    return json.sessionId;
  }

  /**
   * Streamt Tokens via SSE. Callback wird pro Delta + einmal mit done/error
   * aufgerufen. Tool-Calls (navigate_to_page, capture_lead) kommen als
   * `event: tool` Frames durch und werden ueber on.tool weitergegeben.
   */
  async streamMessage(
    sessionId: string,
    content: string,
    language: string,
    on: {
      delta: (text: string) => void;
      tool: (event: { name: string; input: Record<string, unknown>; output: Record<string, unknown> }) => void;
      done: (info: { tokensIn?: number; tokensOut?: number; model?: string }) => void;
      error: (msg: string) => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    const url = `${this.opts.apiBase}/api/web-chat/${this.opts.publicId}/sessions/${sessionId}/messages`;
    const r = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      signal,
      headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
      body: JSON.stringify({ content, language }),
    });
    if (!r.ok || !r.body) {
      on.error(`Server-Fehler ${r.status}`);
      return;
    }
    const reader = r.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let currentEvent: string | null = null;

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      // SSE-Frames sind durch \n\n getrennt
      let idx;
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        // Parse Frame
        const lines = frame.split('\n');
        let data = '';
        currentEvent = null;
        for (const ln of lines) {
          if (ln.startsWith('event:')) currentEvent = ln.slice(6).trim();
          else if (ln.startsWith('data:')) data += ln.slice(5).trim();
        }
        if (!data) continue;
        try {
          const parsed = JSON.parse(data);
          if (currentEvent === 'error') {
            on.error(parsed.message ?? 'Stream-Fehler');
            return;
          }
          if (currentEvent === 'done') {
            on.done(parsed);
            return;
          }
          if (currentEvent === 'tool') {
            on.tool(parsed);
            continue;
          }
          if (parsed.delta) on.delta(parsed.delta);
        } catch {
          // ignorieren
        }
      }
    }
  }
}

export function getOrCreateVisitorToken(): string {
  const KEY = 'gwd-chat-visitor';
  try {
    const existing = localStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    // Private Browsing / blockiert localStorage → Session-Token im Speicher
    return 'tmp-' + Math.random().toString(36).slice(2);
  }
}
